import React, { FC } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  IEventPersonEntity,
  IRecentAppointmentItem,
  IRecentAppointmentItemImt,
} from 'modules/booking/interfaces';
import { getArray } from 'common/utils/typeUtils';
import { ActionsMenu } from 'common/components/index';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import BookingActionItemEdit from 'components/Booking/BookingActionItemEdit';
import BookingActionItemDelete from 'components/Booking/BookingActionItemDelete';
import BookingActionItemRedeem from 'components/Booking/BookingActionItemRedeem';
import BookingActionItemRemind from 'components/Booking/BookingActionItemRemind';
import BookingActionItemRestore from 'components/Booking/BookingActionItemRestore';
import BookingActionItemCancel from 'components/Booking/BookingActionItemCancel';
import BookingActionItemConfirmParticipation from 'components/Booking/BookingActionItemConfirmParticipation';
import BookingActionItemCancelParticipation from 'components/Booking/BookingActionItemCancelParticipation';

const useStyles = makeStyles(() => ({
  menu: {
    minWidth: 'fit-content',
  },
}));

interface IAppointmentMenu {
  userId: number;
  appointment: IRecentAppointmentItemImt;
  onEdit: (appointment: IRecentAppointmentItemImt) => void;
  onRedeem: (appointment: IRecentAppointmentItemImt) => void;
  onDelete: (appointment: IRecentAppointmentItemImt) => void;
  onCancel: (appointment: IRecentAppointmentItemImt) => void;
  onCancelParticipation: (appointment: IRecentAppointmentItemImt) => void;
  onConfirmParticipation: (appointment: IRecentAppointmentItemImt) => void;
  onRemind?: (event: IRecentAppointmentItemImt) => void;
  onRestore?: (event: IRecentAppointmentItem) => void;
}

const AppointmentMenu: FC<IAppointmentMenu> = ({
  userId,
  appointment,
  onEdit,
  onRedeem,
  onCancel,
  onCancelParticipation,
  onConfirmParticipation,
  onDelete,
  onRemind,
  onRestore,
}) => {
  const classes = useStyles();
  const [timezoneMoment] = useTimezoneMoment();
  const eventDate = timezoneMoment(appointment.get('date'));
  const secondsBeforeEvent = eventDate.diff(timezoneMoment(), 'seconds');
  const overbookingType = appointment.get('overbookingType');
  const spotLimited = appointment.get('spotLimited');
  const bookedSpots = appointment.get('bookedSpots') || 0;
  const spotAmount = appointment.get('spotAmount') || 0;
  const isAppointmentCanceled = appointment.get('canceled');
  const appointmentStatus = appointment.get('appointmentStatus');
  const editRestrictionBeforeDays = appointment.get('editRestrictionBeforeDays');
  const cancelRestrictionBeforeDays = appointment.get('cancelRestrictionBeforeDays');
  const persons = getArray<IEventPersonEntity>(appointment.get('persons')?.toJS());

  const onConfirmParticipationClick = (): void => {
    onConfirmParticipation(appointment);
  };

  const onCancelParticipationClick = (): void => {
    onCancelParticipation(appointment);
  };

  const onCancelClick = (): void => {
    onCancel(appointment);
  };

  const onRestoreClick = (): void => {
    if (onRestore) {
      onRestore(appointment.toJS());
    }
  };

  const onRemindClick = (): void => {
    if (onRemind) {
      onRemind(appointment);
    }
  };

  const onRedeemClick = (): void => {
    onRedeem(appointment);
  };

  const onEditClick = (): void => {
    onEdit(appointment);
  };

  const onDeleteClick = (): void => {
    onDelete(appointment);
  };

  return (
    <ActionsMenu horizontal className={classes.menu}>
      <BookingActionItemEdit
        onClick={onEditClick}
        secondsBeforeEvent={secondsBeforeEvent}
        editRestrictionBeforeDays={editRestrictionBeforeDays}
      />

      <BookingActionItemConfirmParticipation
        onClick={onConfirmParticipationClick}
        userId={userId}
        eventPersons={persons}
        appointmentStatus={appointmentStatus}
        overbookingType={overbookingType}
        spotLimited={spotLimited}
        bookedSpots={bookedSpots}
        spotAmount={spotAmount}
        isEventCanceled={isAppointmentCanceled}
      />

      <BookingActionItemCancelParticipation
        onClick={onCancelParticipationClick}
        userId={userId}
        eventPersons={persons}
        appointmentStatus={appointmentStatus}
        isEventCanceled={isAppointmentCanceled}
      />

      <BookingActionItemRedeem
        onClick={onRedeemClick}
        appointmentStatus={appointmentStatus}
        secondsBeforeEvent={secondsBeforeEvent}
        hasScheduledService={appointment.get('hasScheduledService')}
        isEventCanceled={isAppointmentCanceled}
      />

      <BookingActionItemRemind
        onClick={onRemindClick}
        isEventCanceled={appointment.get('canceled')}
      />

      {!appointment.get('canceled') ? (
        <BookingActionItemCancel
          onClick={onCancelClick}
          secondsBeforeEvent={secondsBeforeEvent}
          cancelRestrictionBeforeDays={cancelRestrictionBeforeDays}
        />
      ) : null}

      {appointment.get('canceled') ? (
        <BookingActionItemRestore
          onClick={onRestoreClick}
          secondsBeforeEvent={secondsBeforeEvent}
        />
      ) : null}

      <BookingActionItemDelete onClick={onDeleteClick} />
    </ActionsMenu>
  );
};

export default AppointmentMenu;
